import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Eye,
  EyeOff,
  MoreVertical,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Popover } from './Popover';

/**
 * SortingChip - Unified implementation for mobile and desktop
 */
export function SortingChip({
  item,
  label,
  isMobile,
  onToggle,
  onToggleDirection,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: any) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const directionIcon = (
    <ChevronDown
      size={14}
      className={item.direction === 'asc' ? 'chip__icon--rotated' : ''}
    />
  );

  // Desktop version
  if (!isMobile) {
    return (
      <div className={`chip ${!item.enabled ? 'chip--disabled' : ''}`}>
        <button
          type="button"
          className={`chip__toggle ${!item.enabled ? 'chip__toggle--off' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item);
          }}
          title={item.enabled ? 'Disable' : 'Enable'}
        >
          {item.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <span className="chip__label">{label}</span>
        <div className="chip__buttons">
          <button
            type="button"
            className="chip__button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleDirection(item);
            }}
            disabled={!item.enabled}
            title="Toggle sort direction"
          >
            {directionIcon}
          </button>
          <button
            type="button"
            className="chip__button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp(item);
            }}
            disabled={!item.enabled || !canMoveUp}
            title="Move Left"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            className="chip__button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown(item);
            }}
            disabled={!item.enabled || !canMoveDown}
            title="Move Right"
          >
            <ChevronRight size={14} />
          </button>
          <button
            type="button"
            className="chip__button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            title="Delete sort"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Mobile version - menu driven
  return (
    <>
      <div className={`chip ${!item.enabled ? 'chip--disabled' : ''}`}>
        <button
          type="button"
          className={`chip__toggle ${!item.enabled ? 'chip__toggle--off' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item);
          }}
          title={item.enabled ? 'Disable' : 'Enable'}
        >
          {item.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <span className="chip__label">{label}</span>
        <button
          type="button"
          className="chip__button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          disabled={!item.enabled}
          title="Open menu"
        >
          <MoreVertical size={14} />
        </button>
      </div>
      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        showBackdrop={true}
      >
        <div className="sorting-menu">
          <button
            className="sorting-menu__item"
            onClick={() => {
              onToggleDirection(item);
              setMenuAnchor(null);
            }}
          >
            {directionIcon}
            {item.direction === 'asc' ? 'Descending' : 'Ascending'}
          </button>
          <button
            className="sorting-menu__item"
            onClick={() => {
              onMoveUp(item);
              setMenuAnchor(null);
            }}
            disabled={!canMoveUp}
          >
            <ChevronUp size={14} />
            Move Up
          </button>
          <button
            className="sorting-menu__item"
            onClick={() => {
              onMoveDown(item);
              setMenuAnchor(null);
            }}
            disabled={!canMoveDown}
          >
            <ChevronDown size={14} />
            Move Down
          </button>
          <button
            className="sorting-menu__item sorting-menu__item--danger"
            onClick={() => {
              onDelete(item);
              setMenuAnchor(null);
            }}
          >
            <X size={14} />
            Remove
          </button>
        </div>
      </Popover>
    </>
  );
}

/**
 * FilterChip - Unified implementation for mobile and desktop
 */
export function FilterChip({
  item,
  label,
  isMobile,
  onDelete,
  onToggle,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: any) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Desktop version
  if (!isMobile) {
    return (
      <div className={`chip ${!item.enabled ? 'chip--disabled' : ''}`}>
        <button
          type="button"
          className={`chip__toggle ${!item.enabled ? 'chip__toggle--off' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item);
          }}
          title={item.enabled ? 'Disable' : 'Enable'}
        >
          {item.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <span className="chip__label">{label}</span>
        <div className="chip__buttons">
          <button
            type="button"
            className="chip__button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp(item);
            }}
            disabled={!item.enabled || !canMoveUp}
            title="Move up"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            className="chip__button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown(item);
            }}
            disabled={!item.enabled || !canMoveDown}
            title="Move down"
          >
            <ChevronDown size={14} />
          </button>
          <button
            type="button"
            className="chip__delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            title="Delete filter"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Mobile version - menu driven
  return (
    <>
      <div className={`chip ${!item.enabled ? 'chip--disabled' : ''}`}>
        <button
          type="button"
          className={`chip__toggle ${!item.enabled ? 'chip__toggle--off' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item);
          }}
          title={item.enabled ? 'Disable' : 'Enable'}
        >
          {item.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <span className="chip__label">{label}</span>
        <button
          type="button"
          className="chip__button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          disabled={!item.enabled}
          title="Open menu"
        >
          <MoreVertical size={14} />
        </button>
      </div>
      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        showBackdrop={true}
      >
        <div className="filter-menu">
          <button
            className="filter-menu__item"
            onClick={() => {
              onMoveUp(item);
              setMenuAnchor(null);
            }}
            disabled={!canMoveUp}
          >
            <ChevronUp size={14} />
            Move Up
          </button>
          <button
            className="filter-menu__item"
            onClick={() => {
              onMoveDown(item);
              setMenuAnchor(null);
            }}
            disabled={!canMoveDown}
          >
            <ChevronDown size={14} />
            Move Down
          </button>
          <button
            className="filter-menu__item filter-menu__item--danger"
            onClick={() => {
              onDelete(item);
              setMenuAnchor(null);
            }}
          >
            <X size={14} />
            Remove
          </button>
        </div>
      </Popover>
    </>
  );
}
